import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/auth/AuthProvider";
import { getAuthOptions } from "@/api/auth";
import { HttpError } from "@/api/client";

function sanitizeNext(value: string | null): string {
  if (!value) return "/";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

export function LoginPage() {
  const { login, loginAsHost } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = sanitizeNext(params.get("next"));

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: authOptions } = useQuery({
    queryKey: ["auth", "options"],
    queryFn: getAuthOptions,
    staleTime: 5 * 60_000,
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message);
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleHostLogin() {
    setError(null);
    setSubmitting(true);
    try {
      await loginAsHost();
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message);
      } else {
        setError("Host login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="auth-shell">
      <article className="auth-card">
        <p className="eyebrow">Login</p>
        <h1>Sign in to the flight workspace</h1>
        <p className="lede">
          A signed-in user gets a personal flight library and replay imports
          attached to their account.
        </p>

        {error && (
          <div className="callout danger">
            <strong>Login failed</strong>
            <p>{error}</p>
          </div>
        )}

        <form className="upload-form" onSubmit={handleSubmit}>
          <label className="file-input">
            <span>Username</span>
            <input
              type="text"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. operator01"
              required
              autoComplete="username"
            />
          </label>
          <label className="file-input">
            <span>Password</span>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min. 8 characters"
              required
              autoComplete="current-password"
            />
          </label>
          <button className="button primary" type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {authOptions?.host_login_available && (
          <form
            className="upload-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleHostLogin();
            }}
          >
            <button
              className="button ghost"
              type="submit"
              disabled={submitting}
            >
              Login as host
            </button>
          </form>
        )}

        <p className="form-note">
          Don't have an account?{" "}
          <Link
            className="text-link"
            to={`/register?next=${encodeURIComponent(nextPath)}`}
          >
            Create one
          </Link>
        </p>
      </article>
    </section>
  );
}
